
import { AttributeFilter } from '../entities/entity';
import { CustomFieldType, CustomFieldUtility } from '../entities/fields/custom.field';
import { Job, WorkOrder } from '../entities/job';
import { SystemPriority } from '../entities/shared/system.priority';
import { Priority } from '../entities/tenant/priority';
import { DateUtility } from './date.utility';
import { JobUtility } from './job.utility';

import * as Enumerable from 'linq';

export type JobSort = Record<keyof Job | keyof WorkOrder, any>;

export class JobIndexUtility {

    private static readonly systemPrioritySort = {
        [SystemPriority.P1]: 1000,
        [SystemPriority.P2]: 2000,
        [SystemPriority.P3]: 3000,
        [SystemPriority.P4]: 4000,
        [SystemPriority.P5]: 5000,
    };

    public static getAttributeFilter(job: Job): AttributeFilter[] {

        const filters: AttributeFilter[] = [];;

        filters.push({ k: 'active', v: job?.active });

        if (job.status)
            filters.push({ k: 'jobStatus', v: job?.status, n: job?.status });

        if (job.type)
            filters.push({ k: 'type', v: job?.type?._id, n: job?.type?.name });

        if (job.priority)
            filters.push({ k: 'priority', v: job?.priority?._id, n: job?.priority?.name });

        if (job.customer)
            filters.push({ k: 'customer', v: job?.customer?._id, n: job?.customer?.name });

        if (job.billingCompany)
            filters.push({ k: 'company', v: job?.billingCompany?._id, n: job?.billingCompany?.name });

        if (job.owner)
            filters.push({ k: 'owner', v: job?.owner?._id, n: job?.owner?.name });

        if (job.fieldSupervisor)
            filters.push({ k: 'fieldSupervisor', v: job?.fieldSupervisor?._id, n: job?.fieldSupervisor?.name });

        if (job.createdAt)
            filters.push({ k: 'createdAt', v: DateUtility.getDate(job.createdAt) });

        if (job.dueDate)
            filters.push({ k: 'dueDate', v: DateUtility.getDate(job.dueDate) });

        if (job.completedDate)
            filters.push({ k: 'completedDate', v: DateUtility.getDate(job.completedDate) });

        if (job.finalisedDate)
            filters.push({ k: 'finalisedDate', v: DateUtility.getDate(job.finalisedDate) });

        if (job?.labels?.length)
            filters.push(...job?.labels?.map(label => ({ k: 'tag', v: label._id, n: label.name })));

        if (job?.systemLabels?.length)
            filters.push(...job?.systemLabels?.map(label => ({ k: 'tag', v: label._id, n: label.name })));

        if (job?.zones?.length)
            filters.push(...job?.zones?.map(zone => ({ k: 'zone', v: zone._id, n: zone.name })));

        // if (job.createdAt)
        //     filters.push({ k: 'createdAt', v: DateUtility.getDate(job.createdAt) });

        if (job.policy)
            filters.push({ k: 'policy', v: job?.policy?._id, n: job?.policy?.name });

        if (job.recurringJob)
            filters.push({ k: 'recurringJob', v: job.recurringJob._id, n: job.recurringJob.name });

        if (job.customFields)
            this.addCustomFieldFilters(filters, job.customFields);


        filters.push({ k: 'invoiced', v: (!!job?.invoice || !!job?.invoices?.length) });

        const textFields = [];

        // Text Filters
        if (job.jobNumber) {
            textFields.push(job.jobNumber);

            const friendlyJobNumber = job?.jobNumber?.replace(/^[A-Za-z]*0*/, '')
            if (friendlyJobNumber?.length) textFields.push(friendlyJobNumber);
            //filters.push({ k: 'text', v: job.jobNumber });
        }

        if (job.customerPO) {
            const parts = this.getTextParts(job.customerPO);
            textFields.push(...parts);
        }
        //filters.push({ k: 'text', v: job.customerPO });

        if (job.customerWO) {
            const parts = this.getTextParts(job.customerWO);
            textFields.push(...parts);
        }
        //filters.push({ k: 'text', v: job.customerWO });

        if (job?.customer)
            textFields.push(job?.customer?.name);
        //filters.push({ k: 'text', v: job?.customer?.name });

        if (job.site) {
            textFields.push(job?.site?.name);
            textFields.push(job?.site?.placeId);
            textFields.push(job?.site?.address?.name ?? JobUtility.address(job?.site?.address));

            // filters.push({ k: 'text', v: job?.site?.name });
            // filters.push({ k: 'text', v: job?.site?.placeId });
            // filters.push({ k: 'text', v: JobUtility.address(job?.site?.address) });

            filters.push({
                k: 'timezone',
                v: job.site?.address?.timezone,
            });

            filters.push({
                k: 'state',
                v: job.site?.address?.state,
            });

            filters.push({
                k: 'siteId',
                v: job.site?._id,
            });

        }

        if (job?.name) {
            const parts = this.getTextParts(job.name);
            textFields.push(...parts);
        }

        Enumerable
            .from(job?.workOrders)
            .forEach(wo => {

                filters.push({ k: 'workOrderActive', v: wo?.active });

                if (wo.status)
                    filters.push({ k: 'workOrderStatus', v: wo?.status._id, n: wo?.status?.name });

                if (wo.workCategory)
                    filters.push({ k: 'workCategory', v: wo.workCategory._id, n: wo.workCategory.name });

                if (wo?.labels?.length)
                    filters.push(...wo?.labels?.map(label => ({ k: 'tag', v: label._id, n: label.name })));

                if (wo?.systemLabels?.length)
                    filters.push(...wo?.systemLabels?.map(label => ({ k: 'tag', v: label._id, n: label.name })));

                if (wo?.resources?.length)
                    filters.push(...wo?.resources?.map(resource => ({ k: 'resource', v: resource._id, n: resource.name })));

                if (wo.customFields)
                    this.addCustomFieldFilters(filters, wo.customFields);

                if (wo?.workOrderNumber) {

                    textFields.push(wo?.workOrderNumber);
                    const friendlyWoNumber = wo?.workOrderNumber?.replace(/^[A-Za-z]*0*/, '')

                    if (friendlyWoNumber?.length)
                        textFields.push(friendlyWoNumber);

                    const splitWoNumber = wo?.workOrderNumber?.split('-');
                    const lastWoNumber = splitWoNumber?.length
                        ? splitWoNumber[splitWoNumber.length - 1]
                        : null;

                    if (lastWoNumber) textFields.push(lastWoNumber);
                }

                if (wo?.stageOwner)
                    filters.push({ k: 'owner', v: wo.stageOwner._id, n: wo.stageOwner.name });

                if (wo?.alternateSite) {
                    textFields.push(wo?.alternateSite?.address?.name ?? JobUtility.address(wo.alternateSite?.address));
                    // const address = JobUtility.address(wo.alternateSite?.address);
                    // filters.push({ k: 'text', v: address });

                    filters.push({
                        k: 'timezone',
                        v: wo.alternateSite?.address?.timezone,
                    });

                    filters.push({
                        k: 'state',
                        v: wo.alternateSite?.address?.state,
                    });

                    filters.push({
                        k: 'siteId',
                        v: wo.alternateSite?._id,
                    });

                }

                filters.push({ k: 'billed', v: (!!wo?.bill || !!wo?.bills?.length) });


            });

        const text = textFields
            .filter(x => x?.length)
            .map(x => {
                const words = x
                    .toLowerCase()
                    .split(' ');

                const extendedWords = words
                    .map(x => {

                        const ew = x.indexOf('/') > -1
                            ? [x, ...x.split('/').map(x => x.trim()).flat()]
                            : [x];

                        return ew;

                    })
                    .flat();

                return [...extendedWords];
            })
            .flat();

        filters.push({ k: 'text', v: text });

        return Enumerable
            .from(filters)
            .where(x => x.v !== undefined && x.v !== null && x.v !== '')
            .distinct(x => `${x.k}:${x.v}`)
            .toArray();

        // return filters;

    }

    public static getTextParts(value: string): string[] {
        let parts = [value];
        const guidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
        if (guidPattern.test(value)) {
            //if its a guid, we do not want to split it
           return parts;
        }
        //otherwise, lets try to split it
        const regex = /([A-Za-z]+|\d+)/g;
        const matches = value.match(regex);

        if (matches) {
            const results = matches.map(segment => {
                if (/^\d+$/.test(segment)) {
                    // If the segment is all digits, keep the original and the version without leading zeros
                    const noLeadingZeros = segment.replace(/^0+/, '') || '0'; // Keep a single zero if the segment was "000"
                    return { original: segment, noLeadingZeros: noLeadingZeros };
                }
                return { original: segment, noLeadingZeros: null };
            });
            const allOriginals = Enumerable.from(results).select(x => x.original).toArray();
            const allNoLeadingZeros = Enumerable.from(results).where(x => x.noLeadingZeros != x.original).select(x => x.noLeadingZeros).toArray();
            parts = [...parts, ...allOriginals, ...allNoLeadingZeros];
        }
        return parts;
    }


    public static getSort(job: Job, priorityMap: Enumerable.IDictionary<string, Priority>): JobSort {

        const sort: JobSort = {} as any;

        this.addSortableDates(sort, 'dueDate', job.dueDate);
        this.addSortableDates(sort, 'finalisedDate', job.finalisedDate);
        this.addSortableDates(sort, 'completedDate', job.completedDate);

        if (job?.priority?._id && priorityMap.contains(job?.priority._id)) {
            const priority = priorityMap.get(job?.priority._id);
            const prioritySort = this.systemPrioritySort[priority?.systemPriority] + priority?.sortOrder;
            sort.priority = prioritySort;
        } else {
            sort.priority = 100_000_000;
        }

        return sort;
    }

    public static getPriorityMap(priorities: Priority[]): Enumerable.IDictionary<string, Priority> {

        const priorityMap = Enumerable
            .from(priorities)
            .toDictionary(x => x._id, x => x);

        return priorityMap;
    }

    private static addSortableDates(sort: JobSort, namePrefix: string, date: Date) {

        const asc = `${namePrefix}_asc`;
        const desc = `${namePrefix}_desc`;

        sort[asc] = date ? date : new Date('2100-12-31T00:00:10.000Z')
        sort[desc] = date ? date : new Date('1980-01-01T00:00:10.000Z');

    }

    private static addCustomFieldFilters(filters: AttributeFilter[], customFields: any[]) {

        if (!customFields)
            return;

        const items = Object
            .keys(customFields)
            .map(key => {
                const value = customFields[key];

                switch (CustomFieldUtility.getFieldType(key)) {
                    case CustomFieldType.Date:
                    case CustomFieldType.DateTime: {
                        return value
                            ? { k: key, v: DateUtility.getDate(value) }
                            : null;
                    }

                    case CustomFieldType.Number: {
                        return { k: key, v: Number(value ?? 0) };
                    }

                    case CustomFieldType.Boolean: {
                        return { k: key, v: !!value };
                    }

                    case CustomFieldType.Address: {
                        return value
                            ? { k: key, v: JobUtility.address(value) }
                            : null;
                    }

                    case CustomFieldType.DropdownMulti: {
                        return (!!value && Array.isArray(value))
                            ? { k: key, v: value.map(x => x) }
                            : null
                    }

                    default: {
                        return value
                            ? { k: key, v: value }
                            : null;
                    }
                }
            })
            .filter(x => !!x);

        filters.push(...items);
    }
}
